<template>
    <div class="tree">
        <sp-loader v-show="isLoading"></sp-loader>

        <div class="tree__top-elements">

            <div @click="searchLoseDataAlert" class="tree__search-wrapper">
                <v-text-field
                    v-model="search"
                    class="tree__search"
                    label="Search element"
                    placeholder="Search element"
                    outline
                    single-line
                    full-width
                    prepend-inner-icon="search"
                ></v-text-field>
            </div>

            <div class="tree__info">
                Objects moved across organizations will lose their data within the current organization
            </div>

            <div class="tree__windows-wrapper" :class="{'single-window': singleWindow}">
                <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                        <span
                            v-bind="attrs"
                            v-on="on"
                        >
                            <icon class="tree__icon tree__icon--single" icon="window-single" @click.native="showSingleWindow"></icon>
                        </span>
                    </template>
                    <span>Hide placeholder</span>
                </v-tooltip>

                <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                        <span
                            v-bind="attrs"
                            v-on="on"
                        >
                            <icon class="tree__icon tree__icon--share" icon="window-share" @click.native="showShareWindow"></icon>
                        </span>
                    </template>
                    <span>Show placeholder</span>
                </v-tooltip>
            </div>
        </div>
        <div class="tree-page" :class="{'single-window': singleWindow}">
            <div class="left">
                <draggable v-model="items" :group="{ name: 'people'}" :move="checkMove" @start="startMethod" @end="endMethod" class="draggable-left" draggable="false">
                    <template v-for="item in items" class="draggable-wrapper">
                        <treeview-node
                            :class="[{
                                 'organization': item.type === 'Organization',
                                 'fleet': item.type === 'Fleet',
                                 'vehicle': item.type === 'Vehicle',
                                 'device': item.type === 'Device',
                            }]"
                            :key="item.id"
                            :value="item"
                            :ref="`treeview-node-${item.id}`"
                            :data-id="`${item.id}`"
                            :id="`${item.id}_${item.type}`"
                            tree-name="items"
                            :update-counters="updateCounters"
                            @handleReassignItem="handleReassignItem"
                            :level="0"
                        ></treeview-node>
                    </template>
                </draggable>
            </div>
            <div id="right" class="right">
                <draggable v-model="items2" :group="{ name: 'people'}" :move="checkMove" @start="startMethod" @end="endMethod" class="draggable-right">
                    <template v-for="item in items2">
                        <treeview-node
                            class="placeholder"
                            :class="[{
                                 'organization': item.type === 'Organization',
                                 'fleet': item.type === 'Fleet',
                                 'vehicle': item.type === 'Vehicle',
                                 'device': item.type === 'Device',
                             }]"
                            :key="item.id"
                            :value="item"
                            :ref="`treeview-node-${item.id}`"
                            :data-id="`${item.id}`"
                            :id="`${item.id}_${item.type}`"
                            tree-name="placeholder"
                            :update-counters="updateCounters"
                            @handleReassignItem="handleReassignItem"
                            :level="0"
                        ></treeview-node>
                    </template>
                </draggable>
            </div>
        </div>
        <reassign-tree-item-modal
            v-model="reassignItemModalOpen"
            :item="reassignModalData"
            :handleReassignNode="handleReassignNode"
        />
    </div>
</template>

<script>
import Draggable from 'vuedraggable'
import TreeviewNode from './TreeviewNode'
import SpLoader from '@/components/atoms/Loader'
import Icon from '@/components/atoms/Icon'
import _ from 'lodash-es'
import TreeApi from '@/api/TreeApi'
import OrganizationTreeTransformer from '@/transformers/OrganizationTreeTransformer'
import ReassignTreeItemModal from '@/views/Tree/components/ReassignTreeItemModal'
import {mapActions, mapGetters} from 'vuex'

const treeApi = new TreeApi()
const organizationTreeTransformer = new OrganizationTreeTransformer()

export default {
    name: 'Tree',
    components: {
        TreeviewNode,
        Draggable,
        ReassignTreeItemModal,
        SpLoader,
        Icon
    },
    data () {
        return {
            search: '',
            isLoading: false,
            tree: {
                organizationId: null,
                totalResults: [],
                treeData: Array,
                isTreeChanged: false
            },
            singleWindow: false,
            dragModal: false,
            isMoveAllowed: false,
            reassignItemModalOpen: false,
            reassignModalData: {
                item: null,
                itemType: null
            }
        }
    },
    async mounted () {
        await this.getTree()
        this.latestFullTree = this.items
    },
    computed: {
        ...mapGetters({
            isSearchActive: 'organizationTreeModule/isSearchActive'
        }),
        organizationTreeItems: {
            get () {
                return this.$store.getters['organizationTreeModule/organizationTreeItems']
            },
            set (value) {
                this.$store.commit('organizationTreeModule/setOrganizationTreeItems', value)
            }
        },
        organizationTreePlaceholder: {
            get () {
                return this.$store.getters['organizationTreeModule/organizationTreePlaceholder']
            },
            set (value) {
                this.$store.commit('organizationTreeModule/setOrganizationTreePlaceholder', value)
            }
        },
        items: {
            get () {
                return this.$store.getters['organizationTreeModule/organizationTreeItems']
            },
            set (value) {
                this.$store.commit('organizationTreeModule/setOrganizationTreeItems', value)
            }
        },
        items2: {
            get () {
                return this.$store.getters['organizationTreeModule/organizationTreePlaceholder']
            },
            set (value) {
                this.$store.commit('organizationTreeModule/setOrganizationTreePlaceholder', value)
            }
        },
        sourceItem: {
            get () {
                return this.$store.getters['organizationTreeModule/sourceItem']
            },
            set (value) {
                this.$store.commit('organizationTreeModule/setSourceItem', value)
            }
        },
        destinationItem: {
            get () {
                return this.$store.getters['organizationTreeModule/destinationItem']
            },
            set (value) {
                this.$store.commit('organizationTreeModule/setDestinationItem', value)
            }
        },
        sourceItemIdAndType: {
            get () {
                return this.$store.getters['organizationTreeModule/sourceItemIdAndType']
            },
            set (value) {
                this.$store.commit('organizationTreeModule/setSourceItemIdAndType', value)
            }
        },
        destinationItemIdAndType: {
            get () {
                return this.$store.getters['organizationTreeModule/destinationItemIdAndType']
            },
            set (value) {
                this.$store.commit('organizationTreeModule/setDestinationItemIdAndType', value)
            }
        },
        prevOrganizationTreeItems: {
            get () {
                return this.$store.getters['organizationTreeModule/prevOrganizationTreeItems']
            },
            set (value) {
                this.$store.commit('organizationTreeModule/setPrevOrganizationTreeItems', value)
            }
        },
        prevOrganizationTreePlaceholder: {
            get () {
                return this.$store.getters['organizationTreeModule/prevOrganizationTreePlaceholder']
            },
            set (value) {
                this.$store.commit('organizationTreeModule/setPrevOrganizationTreePlaceholder', value)
            }
        },
        latestFullTree: {
            get () {
                return this.$store.getters['organizationTreeModule/organizationTreeItems']
            },
            set (value) {
                this.$store.commit('organizationTreeModule/setLatestFullTree', value)
            }
        }
    },
    watch: {
        search () {
            if (3 <= this.search.length || 0 === this.search.length) {
                this.getTree()
            }
            if (3 <= this.search.length) {
                this.$store.commit('organizationTreeModule/setIsSearchActive', true)
            }
            if (3 > this.search.length) {
                this.$store.commit('organizationTreeModule/setIsSearchActive', false)
            }
        }
    },
    methods: {
        async getTree () {
            this.isLoading = true

            const response = await treeApi.getTree({
                organizationId: this.organizationId,
                search: this.search
            })
            this.treeData = response
            this.totalResults = response.payload.totalResults

            const treeObject = organizationTreeTransformer.transform(response.payload)

            this.isLoading = false
            this.items = [
                treeObject
            ]

            if (0 < this.organizationTreePlaceholder.length) {
                this.searchFilter(this.items[0], this.organizationTreePlaceholder.map(orgNode => orgNode.id))
            }

            this.prevOrganizationTreeItems = _.cloneDeep(this.items)
            // this.prevOrganizationTreePlaceholder = []
        },
        checkMove (evt) {
            const isVehicleEmpty = evt.to.children.length
            this.isMoveAllowed = false

            if (this.fromPlaceholderToPlaceholder(evt)) {
                return true
            }

            if (this.isAllowedToDrag(evt, 'Organization', 'Organization')) {
                return true
            }

            if (this.isAllowedToDrag(evt, 'Fleet', 'Organization')) {
                return true
            }

            if (this.isAllowedToDrag(evt, 'Vehicle', 'Fleet')) {
                return true
            }

            if ((this.isAllowedToDrag(evt, 'Device', 'Vehicle')) ||
                (this.isAllowedToDrag(evt, 'Device', 'Fleet')) ||
                (this.isAllowedToDrag(evt, 'Device', 'Organization'))) {
                return true
            }

            return false
        },
        isAllowedToDrag (evt, draggedFrom, draggedTo) {
            const draggedItem = evt.dragged.id
            const draggedItemType = draggedItem.split('_')
            const dropTarget = evt.to.id
            const dropType = dropTarget.split('_')
            const fromRightTree = evt.from.closest('.right')
            const toLeftTree = evt.to.closest('.left')

            return (fromRightTree && toLeftTree && draggedFrom === draggedItemType[1] && draggedTo === dropType[1])
        },
        fromPlaceholderToPlaceholder (evt) {
            const itemFromTree = evt.from.className
            const itemToPlaceholder = evt.to.className

            return ('draggable-right' === itemFromTree && 'draggable-right' === itemToPlaceholder)
        },
        startMethod (elem) {
        },
        endMethod (elem) {
            const isElementDraggedFrom = elem.from
            const isElementDraggedTo = elem.to

            this.fromItemIdAndType = elem.from.id
            this.sourceItemIdAndType = elem.item.id
            this.destinationItemIdAndType = elem.to.id

            let destinationItemId
            const sourceItemId = this.sourceItemIdAndType.split('_')[0]

            this.organizationTreeItems = this.items;
            this.organizationTreePlaceholder = this.items2;

            if (elem.to.closest('.left') && elem.from.closest('.left')) {
                // original -> original
                destinationItemId = this.destinationItemIdAndType.split('_')[0]
                const destinationItem = this.getNode(this.organizationTreeItems[0], destinationItemId)
                const sourceItem = this.getNode(this.organizationTreeItems[0], sourceItemId)
                const fromItemId = this.fromItemIdAndType.split('_')[0]
                const fromItem = this.getNode(this.organizationTreeItems[0], fromItemId)

                sourceItem.parent = destinationItem

                this.updateCounters(destinationItem, sourceItem, 'add')
                this.updateCounters(fromItem, sourceItem, 'subtract')
                this.sourceItem = sourceItem
                this.destinationItem = destinationItem
            } else if (elem.to.closest('.left') && !elem.from.closest('.left')) {
                // placeholder -> original
                destinationItemId = this.destinationItemIdAndType.split('_')[0]
                const sourceItem = this.getNode(this.organizationTreeItems[0], sourceItemId)
                const destinationItem = this.getNode(this.organizationTreeItems[0], destinationItemId)
                const fromItemId = this.fromItemIdAndType.split('_')[0]

                let fromItem = null
                if (fromItemId) {
                    this.organizationTreePlaceholder.forEach(orgTree => {
                        const item = this.getNode(orgTree, fromItemId)
                        if (item) fromItem = item
                    })
                }

                sourceItem.parent = destinationItem

                this.updateCounters(destinationItem, sourceItem, 'add')
                this.updateCounters(fromItem, sourceItem, 'subtract')
                this.sourceItem = sourceItem
                this.destinationItem = destinationItem
            } else if (!elem.to.closest('.left') && elem.from.closest('.left')) {
                // original -> placeholder
                let sourceItem
                this.organizationTreePlaceholder.forEach(orgTree => {
                    const item = this.getNode(orgTree, sourceItemId)
                    if (item) sourceItem = item
                })
                destinationItemId = this.destinationItemIdAndType.split('_')[0]
                let destinationItem = null
                if (destinationItemId) {
                    this.organizationTreePlaceholder.forEach(orgTree => {
                        const item = this.getNode(orgTree, destinationItemId)
                        if (item) destinationItem = item
                    })
                }

                const fromItemId = this.fromItemIdAndType.split('_')[0]
                const fromItem = this.getNode(this.organizationTreeItems[0], fromItemId)

                sourceItem.parent = destinationItem

                this.updateCounters(destinationItem, sourceItem, 'add')
                this.updateCounters(fromItem, sourceItem, 'subtract')

                this.$store.commit('organizationTreeModule/setPrevOrganizationTreePlaceholder', _.cloneDeep(this.organizationTreePlaceholder))
                this.$store.commit('organizationTreeModule/setPrevOrganizationTreeItems', _.cloneDeep(this.organizationTreeItems))

            } else if (!elem.to.closest('.left') && !elem.from.closest('.left')) {
                // placeholder -> placeholder
                let sourceItem
                this.organizationTreePlaceholder.forEach(orgTree => {
                    const item = this.getNode(orgTree, sourceItemId)
                    if (item) sourceItem = item
                })
                destinationItemId = this.destinationItemIdAndType.split('_')[0]
                let destinationItem = null
                if (destinationItemId) {
                    this.organizationTreePlaceholder.forEach(orgTree => {
                        const item = this.getNode(orgTree, destinationItemId)
                        if (item) destinationItem = item
                    })
                }

                const fromItemId = this.fromItemIdAndType.split('_')[0]
                let fromItem = null
                if (fromItemId) {
                    this.organizationTreePlaceholder.forEach(orgTree => {
                        const item = this.getNode(orgTree, fromItemId)
                        if (item) fromItem = item
                    })
                }

                sourceItem.parent = destinationItem

                this.updateCounters(destinationItem, sourceItem, 'add')
                this.updateCounters(fromItem, sourceItem, 'subtract')
            }


            if (elem.to.closest('.left') === null) {
                if (process.env.VUE_APP_DEBUG) {
                    console.debug('endMethod', 'tree updated')
                }
                this.prevOrganizationTreeItems = _.cloneDeep(this.items)
                if (process.env.VUE_APP_DEBUG) {
                    console.debug('endMethod', 'placeholder tree updated')
                }
                this.prevOrganizationTreePlaceholder = _.cloneDeep(this.items2)
            }

            if (isElementDraggedTo !== isElementDraggedFrom) {
                this.isTreeChanged = true
            }
            return false
        },
        showSingleWindow () {
            this.singleWindow = true
        },
        showShareWindow () {
            this.singleWindow = false
        },
        searchLoseDataAlert () {
            if (0 === this.items2.length) {
                return
            }
            this.dragModal = !this.dragModal
        },
        confirmTreeModal () {
            this.dragModal = !this.dragModal
        },
        cancelTreeModal () {
            this.dragModal = !this.dragModal

            console.debug('TreeViewNode::cancelTreeModal', this.prevOrganizationTreeItems)

            this.$store.commit('organizationTreeModule/setOrganizationTreeItems', _.cloneDeep(this.prevOrganizationTreeItems))
            this.$store.commit('organizationTreeModule/setOrganizationTreePlaceholder', _.cloneDeep(this.prevOrganizationTreePlaceholder))
        },
        handleReassignItem (itemToReassignData) {
            this.reassignModalData = itemToReassignData
            this.reassignItemModalOpen = true
        },
        updateCounters (initialParentNode, nodeMoved, updateType = 'add') {
            if (!initialParentNode) return
            let { organizationCounter = 0, fleetsCounter = 0, devicesCounter = 0, vehicleCounter = 0 } = nodeMoved

            switch (nodeMoved.type) {
                case 'Organization':
                    organizationCounter += 1
                    break
                case 'Fleet':
                    fleetsCounter += 1
                    break
                case 'Device':
                    devicesCounter += 1
                    break
                default:
                    vehicleCounter += 1
            }

            const addOrSubtractMultiplier = updateType === 'add' ? 1 : -1
            organizationCounter = organizationCounter * addOrSubtractMultiplier
            fleetsCounter = fleetsCounter * addOrSubtractMultiplier
            devicesCounter = devicesCounter * addOrSubtractMultiplier
            vehicleCounter = vehicleCounter * addOrSubtractMultiplier

            let parent = initialParentNode;

            while (parent) {
                if (parent.hasOwnProperty('organizationCounter') && organizationCounter !== 0) parent.organizationCounter += organizationCounter
                if (parent.hasOwnProperty('fleetsCounter') && fleetsCounter !== 0) parent.fleetsCounter += fleetsCounter
                if (parent.hasOwnProperty('devicesCounter') && devicesCounter !== 0) parent.devicesCounter += devicesCounter
                if (parent.hasOwnProperty('vehicleCounter') && vehicleCounter !== 0) parent.vehicleCounter += vehicleCounter
                parent = parent.parent
            }
        },
        treeCountersUpdate (tree) {

        },
        insertNodeIntoTree(node, targetNodeId, nodeToInsert) {
            if (node.id == targetNodeId) {
                if (nodeToInsert) {
                    nodeToInsert.parent = node;
                    node.items.push(nodeToInsert)
                    this.updateCounters(nodeToInsert.parent, nodeToInsert, 'add')
                    return nodeToInsert
                }
            } else if (node.items != null || (Array.isArray(node.items) && node.items.length > 0)) {
                for (let i = 0; i < node.items.length; i++) {
                    return this.insertNodeIntoTree(node.items[i], targetNodeId, nodeToInsert);
                }
            }
        },
        removeNodeFromTree(node, nodeIdToRemove, nodeToRemove) {
            if (node.items != null || (Array.isArray(node.items) && node.items.length > 0)) {
                for (let i = 0; i < node.items.length; i++) {
                    let filtered = node.items.filter(f => f.id == nodeIdToRemove);
                    if (filtered && filtered.length > 0) {
                        node.items = node.items.filter(f => f.id != nodeIdToRemove);
                        this.updateCounters(node, nodeToRemove, 'subtract')
                        return;
                    }
                    this.removeNodeFromTree(node.items[i], nodeIdToRemove, nodeToRemove);
                }
            }
        },
        handleReassignNode(targetNodeId, nodeToMove) {
            this.removeNodeFromTree(this.items[0], nodeToMove.id, nodeToMove)
            this.insertNodeIntoTree(this.items[0], targetNodeId, nodeToMove)
        },
        getParentId (array, id, parentId) {
            return array.some(o => o.id === id || o.items && null !== (parentId = this.getParentId(o.items, id, o.id))) ? parentId : null
        },
        getNode (node, id) {
            if (node.id === id) {
                return node
            }

            for (const node of node.items) {
                const value = this.getNode(node, id)
                if (value) return value
            }
        },
        searchFilter (node, idsToFilterOut) {
            node.items = node.items.filter(childNode => !idsToFilterOut.includes(childNode.id))
            for (const node of node.items) {
                this.searchFilter(node, idsToFilterOut)
            }
        }
    }
}
</script>

<style scoped lang="scss">
.tree {
    position: relative;

    &__info {
        background-color: #ffe7c4;
        font-size: 12px;
        margin: 0 15px 5px;
        display: flex;
        height: max-content;
        padding: 2px 10px;
        border-radius: 8px;

        @media (min-width: 992px) {
            width: 35%;
        }

        @media (min-width: 1440px) {
            width: 30%;
            max-width: 370px;
        }
    }

    &__top-elements {
        display: flex;
    }

    &__assign-wrapper {
        display: flex;
        align-items: center;
        margin-left: 1%;
        margin-right: auto;
        height: 40px;
    }

    &__windows-wrapper {
        display: flex;
        align-items: center;
        margin-right: 1%;
        margin-left: auto;
        border-radius: 6px;
        background-color: #eee;
        height: 40px;
    }

    &__assign {
        font-size: 12px;
        cursor: pointer;

        &--all {
            background-color: #fff;
            padding: 5px;
        }

        &--me {
            background-color: #efefef;
            border-radius: 6px;
            padding: 5px;
            margin-left: 15px;
        }

        span {
            background-color: #dedede;
            border-radius: 50%;
            padding: 5px 9px;
        }
    }

    &__icon {
        margin: 5px 10px;
        cursor: pointer;
        width: 30px;
        height: 30px;
        padding: 6px;
        border-radius: 6px;
        transition: background-color 0.3s;

        &:hover {
            background-color: #fff;
        }

        &--share {
            background-color: #fff;
        }
    }

    &__search-wrapper {
        width: 50%;

        @media (min-width: 992px) {
            width: 30%;
        }
    }

    &__search {
        ::v-deep .v-input__slot {
            border: 2px solid #eeeeee !important;
            border-radius: 10px !important;
            min-height: 40px !important;
        }
    }
}

::v-deep .v-input.v-text-field--single-line input {
    margin-top: 2px !important;
}

::v-deep .v-text-field.v-text-field--enclosed .v-input__prepend-inner {
    margin-top: 7px !important;
}

.assign-active {
    .tree__assign--me {
        background-color: #fff;
    }

    .tree__assign--all {
        background-color: #eee;
    }
}

.single-window {
    .tree__icon--share {
        background-color: #efefef;
        border-radius: 6px;
    }

    .tree__icon--single {
        background-color: #fff;
    }

    .right {
        width: 0;
        display: none;
        animation: sinhleWindow 0.3s;
    }

    .left {
        width: 99%;
        animation: wholeWindow 0.3s;
    }
}

.tree-page {
    display: flex;
    flex-flow: row;
    justify-content: flex-start;
    margin-right: -10px;
}

.left,
.right {
    width: 49%;
}

.right {
    animation: showWindow 0.3s;
}

.left {
    animation: shareWindow 0.3s;
}

.draggable {
    &-left,
    &-right {
        width: 99%;
        border: 2px solid #eeeeee;
        border-radius: 6px;
        height: 80vh;
        padding: 20px 0 20px 0;
        margin: 0 5px 0 5px;
        overflow-y: scroll;

        @media (min-width: 1200px) {
            padding: 20px 30px 20px 10px;
        }
    }

    &-wrapper {
        position: relative;
    }
}

@keyframes wholeWindow {
    from {
        width: 49%;
    }

    to {
        width: 99%;
    }
}

@keyframes shareWindow {
    from {
        width: 99%;
    }

    to {
        width: 49%;
    }
}

@keyframes showWindow {
    from {
        width: 0;
    }

    to {
        width: 49%;
    }
}
</style>
